import React, { useCallback, useEffect, useRef, useState, type ChangeEvent } from "react";
import SpeechRecognition, { useSpeechRecognition } from "react-speech-recognition";
import { useAtom } from "jotai";
import { useLocation, useNavigate } from "react-router-dom";
import ReceptionCharacterCanvas from "~/components/reception/ReceptionCharacterCanvas";
import ReceptionHeader from "~/components/ReceptionHeader";
import "~/css/reception/ReceptionConversationDepartmentPic.css";
import {
  T2SEngineAtom,
  currentSpeakerAtom,
  isAllLoadedAtom,
  languageAtom,
  receptionCompanyIdAtom,
  standbyModeAtom,
  volumeAtom,
} from "~/Atom";
import { talkModes } from "~/Commons";
import { audioManager, playVoice, type PlayVoiceParams } from "~/SoundUtils";
import { api } from "~/api";
import { getCurrentCustomerId } from "~/AtomWithDynamoDB";
import { useTranslation } from "react-i18next";
import { useInactivityTimeout } from "~/utils/receptionUtils";
import FitText from "~/components/reception/FitText";
import { runReceptionBack } from "~/utils/receptionNavigation";
import { ROUTE } from "~/constants/routes";
import { receptionBowEndExpression } from "~/reception/motion/receptionBowMotion";
import * as icon from "~/Icons";
import { getPhoneBooksEntity } from "~/utils/receptionDB";
import type { PhoneBooksEntity } from "~/utils/receptionDB.types";
import {
  type SearchResult,
  type VisitorSelectionNavigationState,
  searchEntities,
  buildGroupConfirmState,
  buildUserConfirmState,
} from "~/utils/visitorSelectionUtils";
import type { VisitorConfirmationNavigationState } from "~/reception/VisitorConfirmationPage";

type ScreenPhase = "robot" | "listening";
type DepartmentPromptKey = "question" | "notFound";

export type DepartmentPicNavigationState = {
  companyName?: string;
  visitorName?: string;
  notFound?: boolean;
};

const MAX_TRANSCRIPT_LENGTH = 1024;
const analyzingBarHeights = [26, 21, 21, 26, 34] as const;

type ExtractedDepartmentInfo = {
  department: string | null;
  personInCharge: string | null;
  personInChargeKana: string | null;
};

// 会社の displayId から検索起点となる rootGroupId を求める
const findRootGroupId = (entity: PhoneBooksEntity, receptionCompanyId: number): number | null => {
  const companyDisplayId = receptionCompanyId === 0 ? null : receptionCompanyId;
  return companyDisplayId !== null
    ? (entity.groupList.find((g) => g.displayId === companyDisplayId)?.groupId ?? null)
    : null;
};

/**
 * 部門・担当者情報を音声で収集し、最大1024文字までテキストを蓄積する受付画面。
 *
 * - 目的: 音声入力および手動編集から部門・担当者情報を抽出し、最終確認後に次画面へ遷移する。
 * - 入力制御: `react-speech-recognition` を利用し、STTの区切り（`listening` が false になるタイミング）ごとに認識結果を蓄積して複数の短い発言を1つの入力にまとめる。
 * - 状態管理: `answerTextRef` に蓄積されたテキストを保持し、テキストエリアで手動編集をサポート。
 * - UI挙動: 「ロボット」フェーズで質問読み上げ → 「リッスン」フェーズでテキスト蓄積表示。UI制御ボタン: クリア、手動編集、送信。
 */
// eslint-disable-next-line complexity
const ReceptionConversationDepartmentPic: React.FC = () => {
  const { t } = useTranslation();
  const titleId = React.useId();
  const navigate = useNavigate();
  const location = useLocation();
  const pageState = (location.state as DepartmentPicNavigationState | null) ?? null;
  const companyName = pageState?.companyName;
  const visitorName = pageState?.visitorName;
  const [notFound, setNotFound] = useState<boolean>(pageState?.notFound ?? false);
  const questionTextKey = notFound
    ? "receptionConversation.groupAndPIC.notFoundText"
    : "receptionConversation.groupAndPIC.questionText";
  const prompts = React.useMemo<Record<DepartmentPromptKey, string>>(
    () => ({
      question: t("receptionConversation.groupAndPIC.questionText"),
      notFound: t("receptionConversation.groupAndPIC.notFoundText"),
    }),
    [t]
  );

  const [T2SEngine] = useAtom(T2SEngineAtom);
  const [currentSpeaker] = useAtom(currentSpeakerAtom);
  const [volume] = useAtom(volumeAtom);
  const [language] = useAtom(languageAtom);
  const [isAllLoaded] = useAtom(isAllLoadedAtom);
  const [, setStandbyMode] = useAtom(standbyModeAtom);
  const [receptionCompanyId] = useAtom(receptionCompanyIdAtom);

  // --- Local State ---
  const [phase, setPhase] = useState<ScreenPhase>("robot");
  const [answerText, setAnswerText] = useState("");
  const [isManualEdit, setIsManualEdit] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);

  // --- Refs (stable, not causing re-render issues) ---
  const askedRef = useRef(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const phaseRef = useRef<ScreenPhase>("robot");
  const answerTextRef = useRef("");
  const hasTranscriptStartedRef = useRef(false);

  // --- Sync atom changes to refs for use in callbacks ---
  useEffect(() => {
    phaseRef.current = phase;
  }, [phase]);

  const { transcript, finalTranscript, listening, resetTranscript } = useSpeechRecognition();

  // --- Setup inactivity timeout ---
  useInactivityTimeout(navigate, transcript);

  useEffect(() => {
    resetTranscript();
  }, [resetTranscript]);

  // isTranscribing が true になったら TTS が終わったサイン → STT を開始する
  useEffect(() => {
    if (!isTranscribing) return;
    if (phaseRef.current !== "robot") return;
    setPhase("listening");
    void SpeechRecognition.startListening({ language });
  }, [isTranscribing, language]);

  const handleBack = useCallback(() => {
    void SpeechRecognition.stopListening();
    navigate(ROUTE.RECEPTION.CONVERSATION.VISITOR, {
      state: { companyName, visitorName },
    });
  }, [navigate, companyName, visitorName]);

  // --- Core: speak question then listen for answer ---
  const speakAndListen = useCallback(
    async (promptKey: DepartmentPromptKey = "question") => {
      setPhase("robot");

      const params: PlayVoiceParams = {
        inputText: prompts[promptKey],
        T2SEngine,
        currentSpeaker,
        speed: 1.0,
        pitch: 0.0,
        intonation: 1.0,
        volume,
        talkingMode: 9,
        setIsTranscribing,
        // 発話終了後にお辞儀モーションを再生する（受付の全画面で統一）。
        endExpression: receptionBowEndExpression(),
      };

      setIsTranscribing(false);
      void SpeechRecognition.stopListening();
      await playVoice(params);
    },
    [T2SEngine, currentSpeaker, prompts, volume]
  );

  // --- AI extraction or fallback ---
  const extractByAiOrFallback = useCallback(
    async (recognized: string): Promise<ExtractedDepartmentInfo> => {
      try {
        const customerId = await getCurrentCustomerId();
        const extracted = await api.openai.extractDepartmentAndPersonInCharge.query({
          text: recognized,
          customerId,
        });
        return {
          department: extracted.department,
          personInCharge: extracted.personInCharge,
          personInChargeKana: extracted.personInChargeKana ?? null,
        };
      } catch (error) {
        console.error("[DepartmentPic] AI extraction failed:", error);
        return {
          department: null,
          personInCharge: null,
          personInChargeKana: null,
        };
      }
    },
    []
  );

  useEffect(() => {
    setStandbyMode(talkModes.standby);
  }, [setStandbyMode]);

  useEffect(() => {
    if (!companyName || !visitorName) {
      runReceptionBack({ kind: "router-history", navigate });
    }
  }, [companyName, visitorName, navigate]);

  // --- Trigger initial question once isAllLoaded becomes true ---
  useEffect(() => {
    if (!isAllLoaded || askedRef.current) return;
    askedRef.current = true;
    const initialPrompt: DepartmentPromptKey = notFound ? "notFound" : "question";
    void speakAndListen(initialPrompt).catch((error) => {
      console.error("[DepartmentPic] speakAndListen failed", error);
    });
  }, [isAllLoaded, notFound, speakAndListen]);

  // --- STTが1区切り終わるたびに認識結果をテキストへ蓄積する ---
  useEffect(() => {
    if (listening || !isTranscribing || isManualEdit || isSubmitting) return;
    const recognized = finalTranscript.trim() || transcript.trim();
    if (!recognized) return;

    const current = answerTextRef.current;
    const separator = current.length > 0 ? " " : "";
    const combined = `${current}${separator}${recognized}`.slice(0, MAX_TRANSCRIPT_LENGTH);
    answerTextRef.current = combined;
    hasTranscriptStartedRef.current = true;
    setAnswerText(combined);

    // 認識結果を消化したらリセットし、続けて次の発話を聞き取る
    resetTranscript();
    void SpeechRecognition.startListening({ language });
  }, [listening, transcript, finalTranscript, language, resetTranscript]);

  // --- 無音でSTTが停止した場合は、リッスンのみ再開して聞き取りを継続する ---
  useEffect(() => {
    if (listening || !isTranscribing || isManualEdit || isSubmitting) return;
    const recognized = finalTranscript.trim() || transcript.trim();
    if (recognized) return; // 認識結果がある場合は上の効果で処理する
    if (hasTranscriptStartedRef.current) {
      hasTranscriptStartedRef.current = false;
      return;
    }
    void SpeechRecognition.startListening({ language });
  }, [transcript, finalTranscript, listening, language]);

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    if (el.value !== answerText) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [answerText]);

  useEffect(() => {
    return () => {
      audioManager.stopCurrentAudio();
      void SpeechRecognition.stopListening();
      resetTranscript();
    };
  }, []);

  // --- UI Actions: Clear, Manual Edit, Submit ---
  const clearAnswer = useCallback(() => {
    answerTextRef.current = "";
    setAnswerText("");
    resetTranscript();
  }, [resetTranscript]);

  const handleClear = useCallback(() => {
    clearAnswer();
  }, [clearAnswer]);

  const handleManualEdit = useCallback(() => {
    void SpeechRecognition.stopListening();
    setIsManualEdit(true);
    window.setTimeout(() => {
      const el = textareaRef.current;
      if (el) {
        el.focus();
        el.selectionStart = el.selectionEnd = el.value.length;
      }
    }, 0);
  }, []);

  const handleAnswerTextChange = useCallback((event: ChangeEvent<HTMLTextAreaElement>) => {
    const nextValue = event.target.value.slice(0, MAX_TRANSCRIPT_LENGTH);
    answerTextRef.current = nextValue;
    setAnswerText(nextValue);
  }, []);

  // --- Not found: 戻って再質問するフロー ---
  const reaskNotFound = useCallback(() => {
    resetTranscript();
    setIsManualEdit(false);
    setPhase("robot");
    setNotFound(true);
    void speakAndListen("notFound");
  }, [resetTranscript, speakAndListen]);

  // --- 複数件ヒット時・IndexedDB 取得失敗時は VisitorSelectionPage で選択させる ---
  const goToVisitorSelection = useCallback(
    (analyzed: ExtractedDepartmentInfo) => {
      navigate(ROUTE.RECEPTION.CONVERSATION.VISITOR_SELECTION, {
        state: {
          companyName,
          visitorName,
          department: analyzed.department ?? undefined,
          personInCharge: analyzed.personInCharge ?? undefined,
          personInChargeKana: analyzed.personInChargeKana ?? undefined,
        } as VisitorSelectionNavigationState,
      });
    },
    [navigate, companyName, visitorName]
  );

  // --- 検索結果が1件に確定したら確認画面へ直行する ---
  const goToSingleResultConfirmation = useCallback(
    (result: Exclude<SearchResult, { kind: "none" }>, analyzed: ExtractedDepartmentInfo) => {
      let nextState: VisitorConfirmationNavigationState;
      if (result.kind === "groups") {
        nextState = buildGroupConfirmState(
          result.items[0].groupId,
          analyzed.department,
          visitorName ?? null,
          companyName ?? null
        );
      } else {
        const user = result.items[0];
        nextState = buildUserConfirmState(
          user,
          user.groupId,
          analyzed.department,
          analyzed.personInCharge,
          visitorName ?? null,
          companyName ?? null
        );
      }
      navigate(ROUTE.RECEPTION.CONVERSATION.VISITOR_CONFIRMATION, {
        state: { ...nextState, fromSelectionAutoRedirect: true },
      });
    },
    [navigate, visitorName, companyName]
  );

  // --- AI抽出結果をもとに電話帳を検索し、件数に応じて再質問・確認・選択へ振り分ける ---
  const handleAnalyzedResult = useCallback(
    async (analyzed: ExtractedDepartmentInfo) => {
      // 担当者名のみで検索するケースでカナが無い場合は即 notFound として再質問へ戻す
      const hasUsableSearch =
        !!analyzed.department || (!!analyzed.personInCharge && !!analyzed.personInChargeKana);
      if (!hasUsableSearch) {
        reaskNotFound();
        return;
      }

      let entity;
      try {
        entity = await getPhoneBooksEntity();
      } catch (error) {
        console.error("[DepartmentPic] getPhoneBooksEntity failed:", error);
      }

      if (!entity) {
        // IndexedDB 取得失敗時は VisitorSelectionPage へフォールバック
        goToVisitorSelection(analyzed);
        return;
      }

      const result = searchEntities(
        entity,
        analyzed.department,
        analyzed.personInCharge,
        analyzed.personInChargeKana,
        findRootGroupId(entity, receptionCompanyId)
      );

      if (result.kind === "none" || result.items.length === 0) {
        reaskNotFound();
        return;
      }

      if (result.items.length === 1) {
        goToSingleResultConfirmation(result, analyzed);
        return;
      }

      goToVisitorSelection(analyzed);
    },
    [reaskNotFound, goToVisitorSelection, goToSingleResultConfirmation, receptionCompanyId]
  );

  const handleSubmit = useCallback(() => {
    const finalAnswer = answerTextRef.current.trim();
    void SpeechRecognition.stopListening();
    clearAnswer();
    if (!companyName || !visitorName || !finalAnswer) return;
    setIsSubmitting(true);

    void (async () => {
      try {
        const analyzed = await extractByAiOrFallback(finalAnswer);
        await handleAnalyzedResult(analyzed);
      } finally {
        setIsSubmitting(false);
      }
    })();
  }, [companyName, visitorName, clearAnswer, extractByAiOrFallback, handleAnalyzedResult]);

  const pageShellClassName =
    phase === "robot"
      ? "reception-conversation-body reception-conversation-department-pic-page__shell reception-conversation-department-pic-page__shell--robot"
      : "reception-conversation-body reception-conversation-department-pic-page__shell reception-conversation-department-pic-page__shell--listening";
  const isAnalyzing = phase === "listening" && !isManualEdit && !isSubmitting;

  return (
    <div className="App-page reception-conversation-page reception-conversation-department-pic-page">
      <ReceptionHeader showBackButton={!notFound} onBackButtonClick={handleBack} />

      <main className="reception-conversation-department-pic-page__content">
        <section className={pageShellClassName} aria-labelledby={titleId}>
          <div className="reception-conversation-department-pic-page__title-group">
            <h1
              id={titleId}
              className="reception-conversation-title reception-conversation-department-pic-page__title typo-h1"
            >
              <FitText>{t(questionTextKey)}</FitText>
            </h1>
          </div>

          {phase === "robot" ? (
            <div className="reception-conversation-department-pic-page__character-panel">
              <ReceptionCharacterCanvas canvasStyle={{ height: "100%", aspectRatio: "1 / 1" }} />
            </div>
          ) : (
            <div className="reception-conversation-analysis reception-conversation-department-pic-page__analysis">
              {isAnalyzing || isSubmitting ? (
                <div
                  className="reception-conversation-department-pic-page__status-badge"
                  role="status"
                  aria-live="polite"
                >
                  {isAnalyzing && (
                    <>
                      <div
                        className="reception-conversation-department-pic-page__status-wave"
                        aria-hidden="true"
                      >
                        {analyzingBarHeights.map((barHeight, index) => (
                          <span
                            key={`left-${index}`}
                            className="reception-conversation-department-pic-page__status-bar"
                            style={{ height: `${barHeight}px` }}
                          />
                        ))}
                      </div>
                      <p className="reception-conversation-department-pic-page__status-text typo-h2">
                        {t("receptionConversation.analyzing")}
                      </p>
                      <div
                        className="reception-conversation-department-pic-page__status-wave reception-conversation-department-pic-page__status-wave--delayed"
                        aria-hidden="true"
                      >
                        {analyzingBarHeights.map((barHeight, index) => (
                          <span
                            key={`right-${index}`}
                            className="reception-conversation-department-pic-page__status-bar"
                            style={{ height: `${barHeight}px` }}
                          />
                        ))}
                      </div>
                    </>
                  )}

                  {isSubmitting && (
                    <>
                      <p className="reception-conversation-department-pic-page__status-text typo-h2">
                        {t("receptionConversation.searching")}
                      </p>
                      <span
                        className="reception-conversation-department-pic-page__spinner"
                        aria-hidden="true"
                      />
                    </>
                  )}
                </div>
              ) : (
                <div
                  className="reception-conversation-department-pic-page__status-badge"
                  aria-hidden="true"
                />
              )}

              <div className="reception-conversation-department-pic-page__editor-stage">
                <div className="reception-conversation-department-pic-page__editor-card">
                  <textarea
                    ref={textareaRef}
                    className="reception-conversation-department-pic-page__textarea typo-text1"
                    value={answerText}
                    disabled={!isManualEdit || isSubmitting}
                    onChange={handleAnswerTextChange}
                    maxLength={MAX_TRANSCRIPT_LENGTH}
                    aria-label="Department and person in charge input"
                    placeholder={t("receptionConversation.groupAndPIC.transcriptPlaceholder")}
                  />

                  <div className="reception-conversation-department-pic-page__manual-action-row">
                    <button
                      type="button"
                      className={`reception-conversation-department-pic-page__button reception-conversation-department-pic-page__button--secondary reception-conversation-department-pic-page__button--manual typo-button${
                        isManualEdit
                          ? " reception-conversation-department-pic-page__button--active"
                          : ""
                      }`}
                      onClick={handleManualEdit}
                      disabled={isSubmitting}
                    >
                      {t("receptionConversation.groupAndPIC.manualEdit")}
                    </button>
                  </div>
                </div>

                <div className="reception-conversation-department-pic-page__button-group">
                  <button
                    type="button"
                    className="reception-conversation-department-pic-page__button reception-conversation-department-pic-page__button--secondary reception-conversation-department-pic-page__button--clear typo-button"
                    onClick={handleClear}
                    disabled={isSubmitting}
                  >
                    {t("receptionConversation.groupAndPIC.clearButton")}
                  </button>
                  <button
                    type="button"
                    className="reception-conversation-department-pic-page__button reception-conversation-department-pic-page__button--primary reception-conversation-department-pic-page__button--send typo-button"
                    onClick={handleSubmit}
                    disabled={!answerText.trim() || isSubmitting}
                  >
                    <icon.icon_send
                      className="reception-conversation-department-pic-page__button-icon"
                      aria-hidden="true"
                    />
                    <span>{t("receptionConversation.groupAndPIC.sendButton")}</span>
                  </button>
                </div>
              </div>
            </div>
          )}
        </section>
      </main>
    </div>
  );
};

export default ReceptionConversationDepartmentPic;
